import { useScopedParams } from "@circle-react/routes/PaywallCheckout/paywallCheckoutRouterHelpers";
import { useLocation } from "@tanstack/react-router";
import { useMemo } from "react";

import type { PaymentValidationParams } from "@/react/types/Paywalls/Checkout/Session";

interface CheckoutRouteParams {
  paymentValidationParams: PaymentValidationParams;
  sessionId: number | null;
  stepId: number | null;
}

/**
 * Extracts checkout-related params from the URL.
 *
 * Used by route wrappers to parse:
 * - Payment validation params (payment_intent, invoice) for verifying payments after redirect
 * - Session ID (query param) for looking up the checkout session
 * - Step ID (route param) for identifying the current step
 *
 * Note: sessionId is passed as a query param because it's not part of the URL path.
 * stepId comes from the route path (e.g., /checkout/:checkoutPath/step/:stepId) and
 * doesn't need to be in query params since the return URL preserves the full path.
 */
export const useCheckoutSessionRouteParams = (): CheckoutRouteParams => {
  const params = useScopedParams({ strict: false });
  const stepIdRoute = params?.stepId;
  const { searchStr } = useLocation();

  return useMemo(() => {
    const searchParams = new URLSearchParams(searchStr);

    // Payment validation params from Stripe redirect
    const paymentIntentId = searchParams.get("payment_intent") || undefined;
    const invoiceId = searchParams.get("invoice") || undefined;
    const paymentValidationParams: PaymentValidationParams = {
      paymentIntentId,
      invoiceId,
      setupIntentId:
        !paymentIntentId && !invoiceId
          ? searchParams.get("setup_intent") ||
            searchParams.get("setup_intent_id") ||
            undefined
          : undefined,
    };

    // Session ID from query param (added to return URL during payment submission)
    const sessionIdParam = searchParams.get("session_id");
    const sessionId =
      sessionIdParam && !Number.isNaN(Number(sessionIdParam))
        ? Number(sessionIdParam)
        : null;

    // Step ID from route path (e.g., /checkout/:checkoutPath/step/:stepId)
    const stepId =
      stepIdRoute && !Number.isNaN(Number(stepIdRoute))
        ? Number(stepIdRoute)
        : null;

    return {
      paymentValidationParams,
      sessionId,
      stepId,
    };
  }, [searchStr, stepIdRoute]);
};
